import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import colors from '../lib/colors';
import OtpCodeInput, { type OtpVerificationIssueKind } from './OtpCodeInput';
import { msg, plural } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import {
    type LocalizedMessage,
    useOptionalResolvedMessage,
    useResolvedMessage,
} from '../lib/i18n/messages';

interface VerificationCodePromptProps {
    email: string;
    instructionText: LocalizedMessage;
    onVerified: () => Promise<void> | void;
    otpType?: 'email' | 'email_change';
    emailLabel?: LocalizedMessage;
    showEmailAddress?: boolean;
    resendCooldown?: number;
    resendLoading?: boolean;
    resendError?: LocalizedMessage | null;
    onResend?: () => void;
    resendLabel?: LocalizedMessage;
    resendCooldownLabel?: LocalizedMessage;
    resendTestID?: string;
    secondaryActionLabel?: LocalizedMessage;
    onSecondaryAction?: () => void;
    secondaryActionTestID?: string;
}

export default function VerificationCodePrompt({
    email,
    instructionText,
    onVerified,
    otpType = 'email',
    emailLabel = msg({ id: 'verification.emailSentTo', message: 'Email sent to:' }),
    showEmailAddress = true,
    resendCooldown = 0,
    resendLoading = false,
    resendError,
    onResend,
    resendLabel = msg({ id: 'verification.resendCode', message: 'Resend code' }),
    resendCooldownLabel = msg({ id: 'verification.resendAvailable', message: 'Resend available' }),
    resendTestID,
    secondaryActionLabel,
    onSecondaryAction,
    secondaryActionTestID,
}: VerificationCodePromptProps) {
    const [otpIssue, setOtpIssue] = useState<OtpVerificationIssueKind | null>(null);
    const resolvedEmailLabel = useResolvedMessage(emailLabel);
    const resolvedInstructionText = useResolvedMessage(instructionText);
    const resolvedResendError = useOptionalResolvedMessage(resendError);
    const resolvedResendLabel = useResolvedMessage(resendLabel);
    const resolvedCooldownLabel = useResolvedMessage(resendCooldownLabel);
    const resolvedSecondaryActionLabel = useOptionalResolvedMessage(secondaryActionLabel);

    useEffect(() => {
        setOtpIssue(null);
    }, [email, otpType]);

    const handleResendPress = () => {
        setOtpIssue(null);
        onResend?.();
    };

    const handleSecondaryActionPress = () => {
        setOtpIssue(null);
        onSecondaryAction?.();
    };

    const recoveryMessage: MessageDescriptor | null = otpIssue === 'invalid_or_expired'
        ? onResend
            ? msg({
                id: 'verification.error.invalidCanResend',
                message: 'That code did not work. Use the latest 6-digit code that arrived, or request a new one.',
            })
            : msg({
                id: 'verification.error.invalidReturn',
                message: 'That code did not work. Return to Paperzilla and request a new sign-in email.',
            })
        : otpIssue === 'unexpected'
            ? msg({
                id: 'verification.error.unexpected',
                message: 'We could not verify that code. Please try again. If the problem continues, request a new one.',
            })
            : null;
    const recoveryHint: MessageDescriptor | null = otpIssue === 'invalid_or_expired'
        ? !onResend
            ? msg({
                id: 'verification.hint.oneCodeOnly',
                message: 'This recovery link can request one code only.',
            })
            : resendCooldown > 0
            ? msg({
                id: 'verification.hint.olderCodes',
                message: 'Older codes may stop working after a newer code is requested.',
            })
            : msg({
                id: 'verification.hint.latestCodeOnly',
                message: 'If another code arrives, use the latest 6-digit code only.',
            })
        : null;
    const recoveryResendLabel = otpIssue === 'invalid_or_expired'
        ? msg({ id: 'verification.sendNewCode', message: 'Send new code' })
        : resendLabel;
    const recoveryCooldownText = otpIssue === 'invalid_or_expired'
        ? msg({
            id: 'verification.cooldown.requestAnother',
            message: plural(resendCooldown, {
                one: 'Request another code in one second',
                other: 'Request another code in # seconds',
            }),
        })
        : msg({
            id: 'verification.cooldown.available',
            message: plural(resendCooldown, {
                one: `${resolvedCooldownLabel} in one second`,
                other: `${resolvedCooldownLabel} in # seconds`,
            }),
        });
    const resolvedRecoveryMessage = useOptionalResolvedMessage(recoveryMessage);
    const resolvedRecoveryHint = useOptionalResolvedMessage(recoveryHint);
    const resolvedRecoveryResendLabel = useResolvedMessage(recoveryResendLabel);
    const resolvedRecoveryCooldownText = useResolvedMessage(recoveryCooldownText);

    return (
        <View style={styles.container}>
            {showEmailAddress && (
                <>
                    <Text style={styles.emailLabel}>{resolvedEmailLabel}</Text>
                    <Text style={styles.emailAddress}>{email}</Text>
                </>
            )}

            <Text style={styles.instructionText}>{resolvedInstructionText}</Text>

            {resolvedResendError && (
                <Text style={styles.resendErrorText}>{resolvedResendError}</Text>
            )}

            <OtpCodeInput
                email={email}
                otpType={otpType}
                onVerified={onVerified}
                onVerificationIssue={setOtpIssue}
            />

            {resolvedRecoveryMessage && (
                <View style={styles.recoveryCard} testID="otp-recovery-message">
                    <Text style={styles.recoveryMessageText}>{resolvedRecoveryMessage}</Text>
                    {resolvedRecoveryHint && (
                        <Text style={styles.recoveryHintText}>{resolvedRecoveryHint}</Text>
                    )}
                </View>
            )}

            {onResend && (
                resendCooldown > 0 ? (
                    <Text style={styles.cooldownText} testID="otp-resend-cooldown">
                        {resolvedRecoveryCooldownText}
                    </Text>
                ) : (
                    <TouchableOpacity
                        onPress={handleResendPress}
                        style={[
                            styles.resendButton,
                            otpIssue === 'invalid_or_expired' && styles.resendButtonPrimary,
                        ]}
                        disabled={resendLoading}
                        testID={resendTestID}
                    >
                        {resendLoading ? (
                            <ActivityIndicator
                                size="small"
                                color={otpIssue === 'invalid_or_expired' ? '#ffffff' : colors.primaryGreen}
                            />
                        ) : (
                            <Text
                                style={[
                                    styles.resendButtonText,
                                    otpIssue === 'invalid_or_expired' && styles.resendButtonTextPrimary,
                                ]}
                            >
                                {resolvedRecoveryResendLabel}
                            </Text>
                        )}
                    </TouchableOpacity>
                )
            )}

            {resolvedSecondaryActionLabel && onSecondaryAction && (
                <TouchableOpacity
                    onPress={handleSecondaryActionPress}
                    style={styles.secondaryActionButton}
                    disabled={resendLoading}
                    testID={secondaryActionTestID}
                >
                    <Text style={styles.secondaryActionText}>{resolvedSecondaryActionLabel}</Text>
                </TouchableOpacity>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        width: '100%',
    },
    emailLabel: {
        fontSize: 14,
        color: colors.text500,
        marginBottom: 4,
        textAlign: 'center',
    },
    emailAddress: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text700,
        marginBottom: 12,
        textAlign: 'center',
    },
    instructionText: {
        fontSize: 14,
        color: colors.text700,
        marginBottom: 8,
        textAlign: 'center',
        lineHeight: 20,
    },
    resendErrorText: {
        fontSize: 14,
        color: colors.errorRed,
        marginBottom: 8,
        textAlign: 'center',
    },
    recoveryCard: {
        width: '100%',
        maxWidth: 360,
        borderWidth: 1,
        borderColor: '#fecaca',
        backgroundColor: '#fff5f5',
        borderRadius: 12,
        paddingHorizontal: 14,
        paddingVertical: 12,
        marginTop: 4,
    },
    recoveryMessageText: {
        fontSize: 14,
        lineHeight: 20,
        color: colors.text900,
        fontWeight: '600',
        textAlign: 'center',
    },
    recoveryHintText: {
        fontSize: 13,
        lineHeight: 18,
        color: colors.text600,
        marginTop: 6,
        textAlign: 'center',
    },
    cooldownText: {
        fontSize: 14,
        color: colors.text500,
        marginTop: 8,
        textAlign: 'center',
    },
    resendButton: {
        minHeight: 36,
        justifyContent: 'center',
        padding: 12,
        marginTop: 8,
    },
    resendButtonPrimary: {
        alignSelf: 'stretch',
        maxWidth: 320,
        backgroundColor: colors.primaryGreen,
        borderRadius: 10,
        paddingHorizontal: 16,
        paddingVertical: 12,
        alignItems: 'center',
        marginTop: 12,
    },
    resendButtonText: {
        color: colors.text700,
        fontSize: 14,
        fontWeight: '500',
        textDecorationLine: 'underline',
    },
    resendButtonTextPrimary: {
        color: '#ffffff',
        fontWeight: '700',
        textDecorationLine: 'none',
    },
    secondaryActionButton: {
        padding: 12,
        marginTop: 4,
    },
    secondaryActionText: {
        color: colors.text600,
        fontSize: 14,
        textDecorationLine: 'underline',
    },
});
